import React from "react";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Button } from "../ui/button";
import { Users } from "lucide-react";
import { toast } from "sonner";

const CABIN_OPTIONS = ["Economy", "Premium Economy", "Business", "First"];

/**
 * Pure helper: build the patch payload for the "Apply cabin class to all
 * travelers" button. Exported so we can unit-test the fan-out shape
 * without needing react-test-renderer (not installed in this app).
 *
 * Returns null when the action should be a no-op (no value, no travelers).
 */
export const buildApplyToAllPatch = (cabinClass, travelers) => {
  if (!cabinClass || !Array.isArray(travelers) || travelers.length === 0) {
    return null;
  }
  return {
    ticket_class: cabinClass,
    traveler_details: travelers.map((td) => ({ ...td, ticket_class: cabinClass })),
  };
};

/**
 * Flight Cabin Class form field — shared by AddSegmentDialog and
 * EditSegmentDialog so the dropdown options, the empty-value sentinel,
 * and the "Apply to all travelers" UX stay identical between Add and Edit.
 *
 * Props:
 *   value           — current segment.ticket_class string ("" when unset).
 *   travelers       — current segment.traveler_details array (used for the
 *                     fan-out button; safe to pass undefined/null).
 *   onChange        — patch callback. Called with `{ ticket_class }` when
 *                     the dropdown changes and with `{ ticket_class,
 *                     traveler_details }` when the user fans the value
 *                     out to all travelers.
 *   labelClassName  — optional override (Add dialog uses 'text-blue-900'
 *                     to match the blue Flight Lookup card).
 *   inputClassName  — optional override for the SelectTrigger (Add dialog
 *                     wants 'bg-white' for the same reason).
 *   testIdPrefix    — prepended to the dropdown's data-testid. Defaults to
 *                     'segment'; pass 'edit-segment' from EditSegmentDialog.
 *
 * The control encodes the "no selection" state as the empty string ""
 * because the backend Segment.ticket_class is Optional[str] and the PUT
 * handler now correctly clears the field when an empty string is sent
 * (see routes/segments.py:CLEARABLE_STRING_FIELDS).
 */
export const SegmentCabinClassField = ({
  value,
  travelers,
  onChange,
  labelClassName = "",
  inputClassName = "",
  testIdPrefix = "segment",
}) => {
  const fanOut = () => {
    const patch = buildApplyToAllPatch(value, travelers);
    if (!patch) return;
    onChange(patch);
    toast.success(
      `Cabin class set to ${value} for ${travelers.length} traveler${travelers.length === 1 ? "" : "s"}`,
    );
  };

  const canFanOut =
    Boolean(value) && Array.isArray(travelers) && travelers.length > 0;

  return (
    <div className="grid gap-2">
      <Label htmlFor={`${testIdPrefix}-cabin-class-select`} className={labelClassName}>
        Cabin Class
      </Label>
      <div className="flex gap-2">
        <Select
          value={value || "none"}
          onValueChange={(v) => onChange({ ticket_class: v === "none" ? "" : v })}
        >
          <SelectTrigger
            id={`${testIdPrefix}-cabin-class-select`}
            data-testid={`${testIdPrefix}-cabin-class-select`}
            className={`flex-1 ${inputClassName}`}
          >
            <SelectValue placeholder="Select cabin class" />
          </SelectTrigger>
          <SelectContent position="popper" className="z-[100]">
            <SelectItem value="none">— Not specified —</SelectItem>
            {CABIN_OPTIONS.map((c) => (
              <SelectItem key={c} value={c}>
                {c}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {canFanOut && (
          <Button
            type="button"
            variant="outline"
            onClick={fanOut}
            data-testid={`${testIdPrefix}-cabin-class-apply-all`}
            title={`Apply ${value} to all ${travelers.length} traveler${travelers.length === 1 ? "" : "s"} on this segment`}
            className="whitespace-nowrap"
          >
            <Users className="h-4 w-4 mr-1.5" />
            Apply to all
          </Button>
        )}
      </div>
    </div>
  );
};

export default SegmentCabinClassField;
